/**
 * Turns raw pointer events into taps, pans, and pinches (CLAUDE.md §5 table).
 * Pure: screen coordinates in, gestures out, no Phaser. Unit-tested with scripted fingers.
 *
 * - A touch that lifts having moved less than `tapThreshold` px is a tap.
 * - Beyond the threshold it is a pan; the first pan carries the whole accumulated delta.
 * - Two fingers form a pinch: `scale` is the change in finger distance since the last event
 *   and (cx, cy) is the midpoint; the midpoint's drift is reported as `dx`/`dy` so the map
 *   follows the fingers. A touch sequence that ever pinched never ends in a tap.
 * - A second tap within DOUBLE_TAP_MS and DOUBLE_TAP_PX of the previous one is a
 *   double-tap (reported instead of the second tap; the first tap was already reported).
 */
export const DOUBLE_TAP_MS = 300;
export const DOUBLE_TAP_PX = 24;
export interface PointerEvent {
  readonly id: number;
  readonly x: number;
  readonly y: number;
}

export type Gesture =
  | { readonly kind: 'tap'; readonly x: number; readonly y: number }
  | { readonly kind: 'doubletap'; readonly x: number; readonly y: number }
  | { readonly kind: 'pan'; readonly dx: number; readonly dy: number }
  | {
      readonly kind: 'pinch';
      readonly scale: number;
      readonly cx: number;
      readonly cy: number;
      readonly dx: number;
      readonly dy: number;
    };

interface Tracked {
  x: number;
  y: number;
  readonly startX: number;
  readonly startY: number;
}

export class GestureRecognizer {
  private readonly pointers = new Map<number, Tracked>();
  private moved = false;
  private pinched = false;
  private lastDist = 0;
  private lastCx = 0;
  private lastCy = 0;
  private lastTap: { t: number; x: number; y: number } | null = null;

  constructor(private readonly tapThreshold = 8) {}

  down(p: PointerEvent): void {
    this.pointers.set(p.id, { x: p.x, y: p.y, startX: p.x, startY: p.y });
    if (this.pointers.size >= 2) {
      this.pinched = true;
      this.moved = true;
      this.measure();
    }
  }

  move(p: PointerEvent): Gesture | null {
    const t = this.pointers.get(p.id);
    if (!t) return null;
    const prevX = t.x;
    const prevY = t.y;
    t.x = p.x;
    t.y = p.y;

    if (this.pointers.size >= 2) {
      const prevDist = this.lastDist;
      const prevCx = this.lastCx;
      const prevCy = this.lastCy;
      this.measure();
      const scale = prevDist > 0 ? this.lastDist / prevDist : 1;
      return {
        kind: 'pinch',
        scale,
        cx: this.lastCx,
        cy: this.lastCy,
        dx: this.lastCx - prevCx,
        dy: this.lastCy - prevCy,
      };
    }

    if (this.pinched) return null; // a finger left after a pinch: ignore until all lift
    if (!this.moved) {
      const far = Math.hypot(p.x - t.startX, p.y - t.startY) >= this.tapThreshold;
      if (!far) return null;
      this.moved = true;
      return { kind: 'pan', dx: p.x - t.startX, dy: p.y - t.startY };
    }
    return { kind: 'pan', dx: p.x - prevX, dy: p.y - prevY };
  }

  up(p: PointerEvent, nowMs: number = Date.now()): Gesture | null {
    const t = this.pointers.get(p.id);
    this.pointers.delete(p.id);
    let result: Gesture | null = null;
    if (t && !this.moved && !this.pinched) {
      const prev = this.lastTap;
      if (
        prev &&
        nowMs - prev.t <= DOUBLE_TAP_MS &&
        Math.hypot(p.x - prev.x, p.y - prev.y) <= DOUBLE_TAP_PX
      ) {
        result = { kind: 'doubletap', x: p.x, y: p.y };
        this.lastTap = null;
      } else {
        result = { kind: 'tap', x: p.x, y: p.y };
        this.lastTap = { t: nowMs, x: p.x, y: p.y };
      }
    }
    if (this.pointers.size === 0) {
      this.moved = false;
      this.pinched = false;
    } else if (this.pointers.size >= 2) {
      this.measure();
    }
    return result;
  }

  cancel(id: number): void {
    this.pointers.delete(id);
    if (this.pointers.size === 0) {
      this.moved = false;
      this.pinched = false;
    }
  }

  private measure(): void {
    const [a, b] = [...this.pointers.values()];
    if (!a || !b) return;
    this.lastDist = Math.hypot(a.x - b.x, a.y - b.y);
    this.lastCx = (a.x + b.x) / 2;
    this.lastCy = (a.y + b.y) / 2;
  }
}
